/**
 * "Your plan, in plain words" — the Granny Flat module's interpretation layer, the same guided-UX
 * pattern as the Aged Care and Downsizer modules (owner direction, 2026-08-25). Sentences that
 * interpret the arrangement — the reasonableness test, homeowner status, the kept former home —
 * then the pension and cashflow movements as ShiftRows with driver-named whys. Content transposed
 * from GrannyFlatInsightPanel's narratives so the closable panel and this always-visible card tell
 * one story.
 *
 * CALCULATION DISCIPLINE — every figure is quoted from `panelModel`, the SAME object that feeds
 * the Financial Impact panel and the insight panel, for whichever view is live (manual settings or
 * the selected Suggestion Engine strategy). Nothing is re-derived here.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, ShiftRow, WhatMovesIt, type EstateDriver } from '../../components/narrative/PlainWords.tsx';
import { PlanPiecesCard, type PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { GrannyFlatInsightFinancials } from './grannyFlatTypes.ts';

interface RecapModel {
    name?: string | null;
    before: { socialSecurity?: number; cashFlow?: number };
    hasArrangement: boolean;
    justMovingIn: boolean;
    amountPaid: number;
    propertyTransferred: number;
    entryContribution: number;
    isHomeowner: boolean;
    keepingHome: boolean;
    keptHomeRented: boolean;
    formerHomeValue: number;
    reasonableValue: number;
    interestValue: number;
    deprivation: number;
    saleFlow?: { homeValue: number; sellingCosts: number; amountPaid: number; residual: number } | null;
    legacyEstimate?: number;
    insightFinancials: GrannyFlatInsightFinancials | null;
}

interface GrannyFlatPlanRecapProps {
    model: RecapModel;
    firstName?: string;
    /**
     * What the estate would be at the horizon if the arrangement simply DID NOT go ahead — the same
     * figure the Financial Impact panel's estate tile uses as its "before", so the two cannot
     * disagree. Null when it could not be computed, in which case the row is omitted rather than
     * compared against today's net worth, which is a different question.
     *
     * Added 2026-08-27: the recap explained the pension and the cashflow and stopped. For a family
     * weighing whether to hand several hundred thousand dollars to a child, what is left behind is
     * usually the question, and it was the one movement with no reason attached.
     */
    baselineLegacy?: number | null;
    legacyYears?: number;
}

const GrannyFlatPlanRecap = ({ model, firstName, baselineLegacy = null, legacyYears = 5 }: GrannyFlatPlanRecapProps) => {
    const fin = model.insightFinancials;
    if ((!model.hasArrangement && !model.justMovingIn) || !fin) return null;

    const name = firstName || 'you';
    const pensionBefore = fin.agePensionBefore ?? 0;
    const pensionAfter = fin.agePensionAfter ?? 0;
    const cashflowBefore = model.before?.cashFlow ?? 0;
    const cashflowAfter = fin.cashflowAfter ?? 0;
    const pensionDelta = Math.round(pensionAfter) - Math.round(pensionBefore);

    /*
     * Driver-named whys. The insight panel's own finding applies here with force: when the former
     * home is KEPT, that — not the gift result — is usually the main event for the pension, so the
     * kept home leads the explanation whenever it is in play.
     */
    const pensionDrivers: string[] = [];
    if (model.keepingHome) {
        pensionDrivers.push(
            `the former home (${formatCurrency(model.formerHomeValue)}) is no longer lived in, so Centrelink now counts it in the assets test${model.keptHomeRented ? ', and the rent it earns is counted as income' : ''}`,
        );
    }
    if (model.deprivation > 0) {
        pensionDrivers.push(
            `${formatCurrency(model.deprivation)} given above the assessed value of the right counts as a gift for five years`,
        );
    }
    if (model.hasArrangement && !model.isHomeowner) {
        pensionDrivers.push(
            `the ${formatCurrency(model.entryContribution)} paid is counted as an asset (though non-homeowner thresholds are higher, and Rent Assistance may apply)`,
        );
    }
    if (model.saleFlow && (model.saleFlow.residual || 0) > 0) {
        pensionDrivers.push(
            `${formatCurrency(model.saleFlow.residual)} of home-sale money not used for the arrangement stays countable`,
        );
    }
    const pensionWhy =
        pensionDrivers.length > 0
            ? `What moves the pension here: ${pensionDrivers.join('; ')}. The Financial Impact panel shows the same before-and-after.`
            : model.justMovingIn
              ? `Nothing is being paid for the right to live there, so there is no gift to count — ${name === 'you' ? 'you are' : `${name} is`} simply assessed as a non-homeowner, with the higher asset limits.`
              : `The amount given sits within the assessed value of the right, and as a homeowner that value is exempt — so what Centrelink counts barely changes.`;

    const cashflowDrivers: string[] = [];
    if (model.keptHomeRented) cashflowDrivers.push('rent from the former home comes in');
    if (model.hasArrangement) cashflowDrivers.push(`the money paid across stops earning where it was`);
    if (pensionDelta !== 0)
        cashflowDrivers.push(
            `the Age Pension is ${pensionDelta > 0 ? 'higher' : 'lower'} (${formatCurrency(pensionBefore)} → ${formatCurrency(pensionAfter)} a year)`,
        );
    const cashflowWhy =
        `A full year of money in minus money out, on the same basis as the Financial Impact panel.` +
        (cashflowDrivers.length > 0 ? ` What changes it: ${cashflowDrivers.join('; ')}.` : '');

    /*
     * THE ESTATE, AND WHAT DECIDES IT. Both sides are projected over the same years by the same
     * engine — the arrangement against not entering it — so the comparison is like for like. The
     * money handed over has genuinely left the estate; the reason says so rather than letting the
     * reader work it out from a smaller number.
     */
    const legacyAfter = model.legacyEstimate ?? null;
    const legacyDelta = baselineLegacy != null && legacyAfter != null ? Math.round(legacyAfter - baselineLegacy) : null;
    const legacyDrivers: EstateDriver[] = [];
    if (model.entryContribution > 0) legacyDrivers.push({ text: "Handed to the family for good — it is theirs now, not an asset that can be called back", amount: model.entryContribution, sign: 'minus' });
    if (pensionDelta !== 0) legacyDrivers.push({ text: `${pensionDelta > 0 ? 'More' : 'Less'} Age Pension, compounding over the ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}`, amount: Math.abs(pensionDelta), suffix: 'a year', sign: pensionDelta > 0 ? 'plus' : 'minus' });
    /*
     * ONLY WHAT ACTUALLY DIFFERS. A kept former home grows in BOTH projections, so listing it as a
     * driver of the difference was wrong — it explained the level, not the gap (caught on screen,
     * 2026-08-27). A home that is SOLD to fund the arrangement genuinely differs: the do-nothing
     * projection still holds it.
     */
    if (model.saleFlow) legacyDrivers.push({ text: `The ${formatCurrency(model.saleFlow.homeValue)} home is sold to fund it, so it is no longer growing in the background${(model.saleFlow.residual || 0) > 0 ? ` — the ${formatCurrency(model.saleFlow.residual)} left over stays invested instead` : ''}`, sign: 'minus' });
    const legacyWhy = (
        <WhatMovesIt
            lead={`Both figures are projected over the same ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} — the arrangement against not entering it — so they can be compared directly.`}
            drivers={legacyDrivers}
        />
    );

    const planPieces: PlanPiece[] = [];
    if (model.amountPaid > 0) planPieces.push({ key: 'payment', name: 'Payment to family for the right to live there', amount: model.amountPaid, what: 'Money handed to a family member in exchange for a lifetime right to live in their home. It has left the estate; Centrelink values the right you bought and treats anything paid above that value as a gift.', glossaryTerm: 'reasonableness test' });
    if (model.propertyTransferred > 0) planPieces.push({ key: 'transfer', name: 'Home transferred to family', amount: model.propertyTransferred, what: 'The home itself is given to the family member in exchange for the right to live there. It is no longer yours to sell, and Centrelink values the right the same way.', glossaryTerm: 'reasonableness test' });
    if (model.saleFlow) planPieces.push({ key: 'sell-home', name: 'Sell the family home', amount: model.saleFlow.homeValue, what: `The home is sold; after ${formatCurrency(model.saleFlow.sellingCosts)} of selling costs the money funds the arrangement, and what is left over is money Centrelink counts.` });
    if (model.keptHomeRented) planPieces.push({ key: 'rent-home', name: 'Keep the former home and rent it out', what: 'The former home is let. The rent counts as income, and the home is counted as an asset once the granny flat is the main home.' });
    if (model.deprivation > 0) planPieces.push({ key: 'gift', name: 'Counted as a gift by Centrelink', amount: model.deprivation, what: `What was paid above Centrelink's reasonable value (${formatCurrency(model.reasonableValue)}) is treated as a gift, and still counted for five years.`, glossaryTerm: 'gifting' });


    return (
        <>
        <PlainWordsCard eyebrow="Your plan summary" className="mt-6">
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
                {model.name && (
                    <p className="text-xs font-medium text-slate-500">
                        Narrating the selected Suggestion Engine approach: {model.name}.
                    </p>
                )}
                {model.justMovingIn ? (
                    <p>
                        Moving in with family <span className="font-semibold text-slate-900">without paying</span> for the
                        right to live there: no granny-flat interest, nothing counts as a gift — {name === 'you' ? 'you are' : `${name} is`}{' '}
                        assessed as a non-homeowner (higher asset limits), though <JargonTerm term="Rent Assistance" />{' '}
                        generally doesn't apply when living with family.
                    </p>
                ) : (
                    <>
                        <p>
                            Giving <span className="font-semibold text-slate-900">{formatCurrency(model.entryContribution)}</span>
                            {model.propertyTransferred > 0 && (
                                <> ({formatCurrency(model.amountPaid)} cash + {formatCurrency(model.propertyTransferred)} in property)</>
                            )}{' '}
                            to family for a lifetime right to live with them. Centrelink values that right at{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(model.interestValue)}</span> — the{' '}
                            <JargonTerm term="reasonableness test" />
                            {model.deprivation > 0 ? (
                                <>
                                    {' '}— and the <span className="font-semibold text-slate-900">{formatCurrency(model.deprivation)}</span>{' '}
                                    given above it counts as a gift under the <JargonTerm term="deprivation" /> rules for five
                                    years.
                                </>
                            ) : (
                                <> — and everything given sits within it, so none of it counts as a gift.</>
                            )}
                        </p>
                        <p>
                            Because the total given is {model.isHomeowner ? 'above' : 'at or below'} the{' '}
                            <JargonTerm term="Extra Allowable Amount" /> ({formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}), Centrelink
                            treats {name} as a{' '}
                            <span className="font-semibold text-slate-900">{model.isHomeowner ? 'homeowner' : 'non-homeowner'}</span>
                            {model.isHomeowner
                                ? ' — the right is exempt from the assets test.'
                                : ' — the amount paid is counted as an asset, but the higher asset limits apply and Rent Assistance may too.'}
                        </p>
                        {model.keepingHome && (
                            <p>
                                The former home is <span className="font-semibold text-slate-900">kept</span>
                                {model.keptHomeRented ? ' and rented out' : ''} — no one in the household lives in it any more,
                                so Centrelink <span className="font-semibold text-slate-900">counts it</span> in the assets test
                                {model.keptHomeRented ? ', with the rent counted as income' : ''}. This is usually the biggest
                                driver of the pension change below.
                            </p>
                        )}
                        {model.saleFlow && (
                            <p>
                                Selling the {formatCurrency(model.saleFlow.homeValue)} home funds the arrangement
                                {(model.saleFlow.residual || 0) > 0 && (
                                    <>
                                        ; the <span className="font-semibold text-slate-900">{formatCurrency(model.saleFlow.residual)}</span>{' '}
                                        left over stays countable
                                    </>
                                )}
                                .
                            </p>
                        )}
                    </>
                )}
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
                <ShiftRow variant="expanded" label="Age Pension" before={pensionBefore} after={pensionAfter} why={pensionWhy} />
                <ShiftRow variant="expanded" label="Yearly cashflow" before={cashflowBefore} after={cashflowAfter} why={cashflowWhy} />
                {legacyDelta != null && (
                    <ShiftRow
                        variant="expanded"
                        label={`Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'} (vs not entering)`}
                        before={baselineLegacy as number}
                        after={legacyAfter as number}
                        suffix=""
                        why={legacyWhy}
                    />
                )}
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
                For the Age Pension and cashflow, "before" is today; "after" matches the Financial Impact panel beside
                it. The estate compares two projections over the same years, not today&apos;s figure.
            </p>
        </PlainWordsCard>
        <PlanPiecesCard className="mt-4" pieces={planPieces} />
        </>
    );
};

export default GrannyFlatPlanRecap;
